import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Bookmark as BookmarkIcon } from "@material-ui/icons";
import { withRouter } from "react-router-dom";
import { Box, CircularProgress, Tooltip } from "@material-ui/core";
import _ from "lodash";
import { withAlert } from "react-alert";
import { withTranslation } from "react-i18next";
import { LIST_PAGE_SIZE } from "../../../constants/listPagination";
import TableGrid from "../../../waybee-ui/TableGrid";
import TeacherService from "../../../services/TeacherService";
import { rememberClass } from "../../../utils/classDirectory";

const LoadContainer = styled.div`
  text-align: center;
  margin: 8px;
`;

const DisabledRowWrap = styled.div`
  opacity: 0.55;
`;

class ClassesList extends Component {
  constructor(props) {
    super(props);
    this.loadSeq = 0;
    const initialClasses = props.classes || [];
    this.state = {
      classes: initialClasses,
      filteredClasses: initialClasses,
      skip: 0,
      total: initialClasses.length,
      loading: false,
      useServerPagination: !props.classes
    };
  }

  componentDidMount() {
    localStorage.removeItem("class");
    const { classes } = this.props;
    if (classes) {
      this.setState({
        classes,
        filteredClasses: classes,
        total: classes.length,
        useServerPagination: false
      });
      return;
    }
    this.loadClasses(true);
    window.addEventListener("scroll", this.handleScroll);
  }

  componentDidUpdate(prevProps) {
    const { filter, classes } = this.props;
    if (classes && classes !== prevProps.classes) {
      this.setState({
        classes,
        filteredClasses: classes,
        total: classes.length,
        useServerPagination: false
      });
      return;
    }
    if (!classes && prevProps.filter !== filter) {
      this.setState({ skip: 0 }, () => this.loadClasses(true));
    } else if (classes && prevProps.filter !== filter) {
      this.filterClassesLocal(filter);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll = () => {
    const { useServerPagination } = this.state;
    if (!useServerPagination) return;
    const container = document.documentElement;
    if (
      container.scrollTop + window.innerHeight >=
      container.offsetHeight - 2
    ) {
      this.loadMoreClasses();
    }
  };

  filterClassesLocal = filter => {
    this.setState({ loading: true });
    const { classes } = this.state;
    const filteredClasses = _.filter(classes, grade =>
      _.includes(
        (grade.name || "").toLowerCase(),
        (filter.name || "").toLowerCase()
      )
    );
    this.setState({ filteredClasses, loading: false });
  };

  loadClasses = async (reset = false) => {
    const { loading, skip } = this.state;
    const { filter } = this.props;
    if (loading && !reset) return;

    this.loadSeq += 1;
    const seq = this.loadSeq;
    const currentSkip = reset ? 0 : skip;

    this.setState(
      reset
        ? { loading: true, filteredClasses: [], classes: [], skip: 0 }
        : { loading: true }
    );

    try {
      const search = filter && filter.name ? filter.name.trim() : "";
      const res = await TeacherService.getClasses({
        search: search || undefined,
        limit: LIST_PAGE_SIZE,
        skip: currentSkip
      });
      if (seq !== this.loadSeq) return;

      const items = (res && res.items) || (Array.isArray(res) ? res : []);
      const total =
        res && typeof res.total === "number"
          ? res.total
          : Number(res && res.total) || items.length;

      this.setState(prev => {
        const next = reset ? items : prev.filteredClasses.concat(items);
        return {
          filteredClasses: next,
          classes: next,
          total,
          skip: currentSkip + items.length,
          loading: false,
          useServerPagination: true
        };
      });
    } catch (e) {
      if (seq !== this.loadSeq) return;
      const { alert, t } = this.props;
      this.setState({ loading: false });
      alert.show(t("classes.list.errorFetch"), {
        title: t("common.actions.error")
      });
    }
  };

  loadMoreClasses = () => {
    const { filteredClasses, total, loading, useServerPagination } = this.state;
    if (!useServerPagination || loading || filteredClasses.length >= total) {
      return;
    }
    this.loadClasses(false);
  };

  isClassAccessible = gradeId => {
    const { accessibleClassIds } = this.props;
    if (accessibleClassIds == null) return true;
    return accessibleClassIds.some(id => String(id) === String(gradeId));
  };

  onSelectItem = grade => {
    if (!this.isClassAccessible(grade.id)) return;
    const { history } = this.props;
    rememberClass(grade);
    localStorage.setItem("class", grade.id);
    history.push(`/classes/${grade.id}`);
  };

  renderRow = grade => {
    const { size, t } = this.props;
    const accessible = this.isClassAccessible(grade.id);

    const row = (
      <TableGrid.Row
        key={`${grade.key}`}
        actionIcon={accessible}
        button={accessible}
        onClick={accessible ? () => this.onSelectItem(grade) : undefined}
      >
        <TableGrid.Title
          {...(size === "compact"
            ? {
                xs: 12
              }
            : {
                xs: 12,
                sm: 4
              })}
        >
          <Box {...(size === "compact" ? { ml: 1, mt: 1 } : {})}>
            {grade.name}
          </Box>
        </TableGrid.Title>
        <TableGrid.Text
          {...(size === "compact"
            ? {
                xs: 12
              }
            : {
                xs: 12,
                sm: 4
              })}
        >
          <Box {...(size === "compact" ? { m: 1 } : {})}>{grade.period}</Box>
        </TableGrid.Text>
        {grade.studentCount && (
          <TableGrid.Text
            {...(size === "compact"
              ? {
                  xs: 12
                }
              : {
                  xs: 12,
                  sm: 4
                })}
          >
            <Box
              alignItems="center"
              display="flex"
              {...(size === "compact"
                ? {
                    ml: 0.5,
                    mb: 1
                  }
                : {})}
            >
              <BookmarkIcon />
              {t("classes.list.studentsCount", { count: grade.studentCount })}
            </Box>
          </TableGrid.Text>
        )}
      </TableGrid.Row>
    );

    if (accessible) return row;

    return (
      <Tooltip
        key={`${grade.key}-disabled`}
        title={t("classes.list.noAccess")}
        placement="top"
      >
        <DisabledRowWrap>{row}</DisabledRowWrap>
      </Tooltip>
    );
  };

  render() {
    const { filteredClasses, loading } = this.state;
    const { className, size } = this.props;

    if (loading && filteredClasses.length === 0)
      return (
        <LoadContainer>
          <CircularProgress />
        </LoadContainer>
      );

    return (
      <>
        <TableGrid className={className} fillParent={size === "compact"}>
          {filteredClasses.map(grade => this.renderRow(grade))}
        </TableGrid>
        {loading && filteredClasses.length > 0 ? (
          <LoadContainer>
            <CircularProgress size={24} />
          </LoadContainer>
        ) : null}
      </>
    );
  }
}

ClassesList.defaultProps = {
  filter: null,
  classes: null,
  accessibleClassIds: null,
  className: "",
  size: "normal"
};

ClassesList.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  history: PropTypes.object.isRequired,
  filter: PropTypes.shape({
    name: PropTypes.string,
    period: PropTypes.string,
    teacher: PropTypes.number
  }),
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      key: PropTypes.string,
      name: PropTypes.string,
      period: PropTypes.string,
      studentCount: PropTypes.number
    })
  ),
  accessibleClassIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  className: PropTypes.string,
  size: PropTypes.oneOf(["compact", "normal"]),
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withAlert()(withRouter(withTranslation()(ClassesList)));
